/**
 * Comprobación con un extracto (pura). Port fiel de reconcileWithStatement_ / reconcileMovementsOnly_.
 * Con saldo tras cada movimiento: compara día a día el saldo del banco con el de la hoja.
 * Sin saldo (MyInvestor): compara solo los movimientos del periodo.
 */
import type { ExistingForImport, ImportPlan, ImportRow } from './import.js';

const round = (n: number) => Math.round(n * 100) / 100 || 0;   // sin «-0»
const iso = (k: string) => (/^\d{8}$/.test(k ?? '') ? `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}` : '');

export interface ReconcileInput { rows: ImportRow[]; plan: Pick<ImportPlan, 'items'>; sheetTxns: ExistingForImport[]; startBalance: number; minDate?: string }

export function reconcileWithStatement(p: ReconcileInput) {
  const rows = p.rows.filter(r => r.balanceAfter !== null && r.balanceAfter !== undefined);
  if (!rows.length) return reconcileMovementsOnly(p);
  let up = 0, down = 0;
  for (let i = 1; i < rows.length; i++) { if (rows[i]!.date > rows[i - 1]!.date) up++; else if (rows[i]!.date < rows[i - 1]!.date) down++; }
  const ordered = up >= down ? rows : rows.slice().reverse();
  const bankEod = new Map<string, number>();
  ordered.forEach(r => bankEod.set(r.date, r.balanceAfter!));
  const allDays = [...bankEod.keys()].sort();
  const days = p.minDate ? allDays.filter(d => d >= p.minDate!) : allDays;
  if (!days.length) throw new Error('El extracto no cubre ningún día desde el inicio de la hoja');
  const first = days[0]!, last = days[days.length - 1]!;
  const prevDay = allDays.filter(d => d < first).pop();
  const firstRow = ordered.find(r => r.date === first)!;
  const bankOpening = prevDay ? bankEod.get(prevDay)! : round(firstRow.balanceAfter! - firstRow.amount);

  const matchedBankDate = new Map<string, string>();
  p.plan.items.forEach(it => { if (it.status === 'duplicate' && it.matchTxnId) matchedBankDate.set(it.matchTxnId, it.date); });
  const sheetDated = p.sheetTxns.map(t => ({ t, day: matchedBankDate.get(t.txnId) || iso(t.dateKey) || iso(t.valueDateKey) }));
  const before = sheetDated.filter(x => x.day < first).reduce((s, x) => s + x.t.amount, 0);
  const sheetAt = (d: string) => round(p.startBalance + sheetDated.filter(x => x.day <= d).reduce((s, x) => s + x.t.amount, 0));
  const openingDiff = round(bankOpening - (p.startBalance + before));
  const missing = p.plan.items.filter(i => i.status !== 'duplicate' && i.date >= first);
  const matchedIds = new Set(matchedBankDate.keys());
  const extra = sheetDated.filter(x => x.day >= first && x.day <= last && !matchedIds.has(x.t.txnId));

  const changes: Array<{ date: string; diff: number; change: number; missing: unknown[]; extra: unknown[] }> = [];
  let prev = openingDiff;
  for (const d of days) {
    const diff = round(bankEod.get(d)! - sheetAt(d));
    if (Math.abs(diff - prev) >= 0.005) {
      changes.push({
        date: d, diff, change: round(diff - prev),
        missing: missing.filter(i => i.date === d).map(i => ({ description: i.description, amount: i.amount, status: i.status, matchDescription: i.matchDescription })),
        extra: extra.filter(x => x.day === d).map(x => ({ txnId: x.t.txnId, description: x.t.description, amount: x.t.amount, row: x.t.row })),
      });
    }
    prev = diff;
  }
  return {
    period: { from: first, to: last }, bankOpening, openingDiff,
    finalDiff: round(bankEod.get(last)! - sheetAt(last)),
    sheetBeforePeriod: round(p.startBalance + before),
    changes, missingCount: missing.length, extraCount: extra.length, matched: matchedIds.size,
  };
}

export function reconcileMovementsOnly(p: ReconcileInput) {
  const all = p.rows.map(r => r.date).filter(Boolean).sort();
  const inRange = all.filter(d => !p.minDate || d >= p.minDate);
  if (!inRange.length) throw new Error('El extracto no cubre ningún día desde el inicio de la hoja');
  const first = inRange[0]!, last = inRange[inRange.length - 1]!;
  const matchedBankDate = new Map<string, string>();
  p.plan.items.forEach(it => { if (it.status === 'duplicate' && it.matchTxnId) matchedBankDate.set(it.matchTxnId, it.date); });
  const sheetIn = p.sheetTxns.map(t => ({ t, day: matchedBankDate.get(t.txnId) || iso(t.dateKey) || iso(t.valueDateKey) }))
    .filter(x => x.day >= first && x.day <= last);
  const missing = p.plan.items.filter(i => i.status !== 'duplicate' && i.date >= first)
    .map(i => ({ date: i.date, description: i.description, amount: i.amount, status: i.status, matchDescription: i.matchDescription }));
  const extra = sheetIn.filter(x => !matchedBankDate.has(x.t.txnId))
    .map(x => ({ date: x.day, txnId: x.t.txnId, description: x.t.description, amount: x.t.amount, row: x.t.row }));
  return {
    mode: 'movements' as const, period: { from: first, to: last },
    bankNet: round(p.rows.filter(r => r.date >= first).reduce((s, r) => s + r.amount, 0)),
    sheetNet: round(sheetIn.reduce((s, x) => s + x.t.amount, 0)),
    missing, extra, missingCount: missing.length, extraCount: extra.length, matched: matchedBankDate.size,
  };
}

/* ================= Cuadre en vivo (revisión de una importación) ================= */

/** Movimiento de la cuenta en la app (todos, con o sin Id: todos cuentan en el saldo). */
export interface AppMovement { id: string; date: string; amount: number; concept: string; origin?: string }

/** Lo que el usuario ha decidido para cada fila del extracto. */
export interface LiveDecision {
  /** Se importa como movimiento nuevo. */
  include: boolean;
  /** Emparejada con este movimiento de la app ('' = no): «es el mismo». */
  pairWith: string;
  /** En una pareja con importe distinto: corregir el de la app al del banco. */
  fixAmount: boolean;
}

export interface LiveInput {
  rows: ImportRow[];
  items: Array<Pick<import('./import.js').ImportItem, 'fileIndex' | 'date' | 'amount' | 'description' | 'status' | 'matchTxnId'>>;
  decisions: Map<number, LiveDecision>;
  app: AppMovement[];
  /** Saldo inicial + archivado de la cuenta. */
  startBalance: number;
  minDate?: string;
  /** Saldo del banco cuando las filas no lo traen (sincronización): a esa fecha. */
  bankBalance?: { amount: number; date: string } | null;
  /** Ajuste de saldo que se creará al guardar (cuenta antes del extracto). */
  adjustment?: number;
}

export interface LiveLine { fileIndex: number; date: string; description: string; amount: number }
export interface AppLine { id: string; date: string; concept: string; amount: number; origin?: string }

/**
 * Cuadre exacto en la fecha final del extracto (L). Con D0 = banco antes del extracto − app antes del extracto:
 *   diferencia = D0 + U (extracto sin pareja) − E (app sin pareja entre F y L) + Δ (parejas con importe distinto) + hueco
 * donde «hueco» solo aparece si el extracto no encadena sus propios saldos (faltan filas). Una pareja cuenta en la
 * fecha del banco. Lo de la app posterior a L no cuenta. Tras guardar: diferencia − importados − importes corregidos − ajuste.
 */
export function reconcileLive(p: LiveInput) {
  const inRange = (d: string) => !p.minDate || d >= p.minDate;
  const fileRows = p.rows.filter(r => inRange(r.date));
  if (!fileRows.length) throw new Error('El extracto no cubre ningún día desde el inicio del libro');
  const itemBy = new Map(p.items.map(i => [i.fileIndex, i]));
  // Parejas: las seguras del plan y las que el usuario confirma («es el mismo» o vinculada a mano)
  const pairOf = new Map<number, string>(), taken = new Set<string>();
  for (const r of fileRows) {
    const it = itemBy.get(r.fileIndex), d = p.decisions.get(r.fileIndex);
    const id = d?.pairWith || (it?.status === 'duplicate' ? it.matchTxnId : '');
    // Cada movimiento de la app empareja con una sola fila del extracto
    if (id && !taken.has(id) && p.app.some(a => a.id === id)) { pairOf.set(r.fileIndex, id); taken.add(id); }
  }
  const pairedBankDate = new Map<string, string>();
  for (const r of fileRows) { const id = pairOf.get(r.fileIndex); if (id) pairedBankDate.set(id, r.date); }
  const eff = (a: AppMovement) => pairedBankDate.get(a.id) ?? a.date;

  // Saldos del banco: con saldo en cada fila, en orden de cobro (no de fecha: un cobro con fecha atrasada no rompe nada)
  const withBal = fileRows.filter(r => r.balanceAfter !== null && r.balanceAfter !== undefined);
  const dates = fileRows.map(r => r.date).sort();
  const F = dates[0]!;
  let L = dates[dates.length - 1]!, bankStart: number | null = null, bankEnd: number | null = null, gap = 0;
  const sumRows = round(fileRows.reduce((t, r) => t + r.amount, 0));
  if (withBal.length === fileRows.length) {
    let up = 0, down = 0;
    for (let i = 1; i < p.rows.length; i++) { if (p.rows[i]!.date > p.rows[i - 1]!.date) up++; else if (p.rows[i]!.date < p.rows[i - 1]!.date) down++; }
    const ordered = up >= down ? fileRows : fileRows.slice().reverse();
    bankStart = round(ordered[0]!.balanceAfter! - ordered[0]!.amount);
    bankEnd = ordered[ordered.length - 1]!.balanceAfter!;
    gap = round(bankEnd - bankStart - sumRows);
  } else if (p.bankBalance) {
    L = p.bankBalance.date > L ? p.bankBalance.date : L;
    bankEnd = p.bankBalance.amount;
    bankStart = round(bankEnd - sumRows);
  }

  const pairedIds = new Set(pairOf.values());
  const before = round(p.startBalance + p.app.filter(a => eff(a) < F).reduce((t, a) => t + a.amount, 0));
  const appEnd = round(p.startBalance + p.app.filter(a => eff(a) <= L).reduce((t, a) => t + a.amount, 0));
  const line = (r: ImportRow): LiveLine => ({ fileIndex: r.fileIndex, date: r.date, description: r.description, amount: r.amount });
  const unmatched = fileRows.filter(r => !pairOf.has(r.fileIndex)).map(line);
  const extra: AppLine[] = p.app.filter(a => !pairedIds.has(a.id) && eff(a) >= F && eff(a) <= L).map(a => ({ id: a.id, date: a.date, concept: a.concept, amount: a.amount, origin: a.origin }));
  const later: AppLine[] = p.app.filter(a => !pairedIds.has(a.id) && eff(a) > L).map(a => ({ id: a.id, date: a.date, concept: a.concept, amount: a.amount, origin: a.origin }));
  const appById = new Map(p.app.map(a => [a.id, a]));
  const amountDiffs = fileRows.filter(r => pairOf.has(r.fileIndex) && Math.abs(r.amount - appById.get(pairOf.get(r.fileIndex)!)!.amount) >= 0.005)
    .map(r => { const a = appById.get(pairOf.get(r.fileIndex)!)!; return { fileIndex: r.fileIndex, id: a.id, date: r.date, description: r.description, bank: r.amount, app: a.amount, diff: round(r.amount - a.amount) }; });
  const sum = (l: Array<{ amount: number }>) => round(l.reduce((t, x) => t + x.amount, 0));

  // Lo que cambia al guardar
  const imported = round(fileRows.filter(r => !pairOf.has(r.fileIndex) && p.decisions.get(r.fileIndex)?.include).reduce((t, r) => t + r.amount, 0));
  const fixed = round(amountDiffs.filter(x => p.decisions.get(x.fileIndex)?.fixAmount).reduce((t, x) => t + x.diff, 0));
  const adjustment = round(p.adjustment ?? 0);

  const hasBank = bankEnd !== null;
  const d0 = hasBank ? round(bankStart! - before) : null;
  const diff = hasBank ? round(bankEnd! - appEnd) : null;
  return {
    from: F, to: L, bankStart, bankEnd, appBefore: before, appEnd,
    d0, unmatched, extra, later, amountDiffs, gap,
    boxes: { u: sum(unmatched), e: sum(extra), delta: round(amountDiffs.reduce((t, x) => t + x.diff, 0)) },
    diff, imported, fixed, adjustment,
    predicted: diff === null ? null : round(diff - imported - fixed - adjustment),
    appAfter: round(appEnd + imported + fixed + adjustment),
  };
}

export type LiveReconcile = ReturnType<typeof reconcileLive>;
